import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { withPageExtent } from '@peruselab/structure-editor/domain/pageExtent';
import { createEmptyDrawingDocument } from '@peruselab/structure-editor/domain/graphDrawing';
import {
  commitHistory,
  createDocumentHistory,
  redoHistory,
  undoHistory,
  type DocumentHistory
} from '@peruselab/structure-editor/domain/history';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { EditorSelection } from '@peruselab/structure-editor/domain/editor';
import { emptySelection } from '@peruselab/structure-editor/domain/editor';
import type { DrawingDocument } from '../generated/drawing-contract';

/**
 * Document lifecycle + undo/redo history for useDrawingDocument.ts (roadmap item 35). Owns
 * `history` and `isOversizedImport` (sticky, not re-derived from the current
 * coordinateSize/paperSize comparison on every render -- a document's own coordinate space can
 * legitimately exceed the live paperSize just because the window shrank, indistinguishable from
 * "this was imported larger than the canvas on purpose" using only the current sizes. Only an
 * import that actually needed more room sets this, and only replacing the document clears it).
 *
 * `setSelection` is a parameter, not owned here: `replaceDocument` and the caller-driven
 * clear-on-clearDrawing path both need to reset selection alongside the document, and threading
 * the raw setter in avoids a circular dependency with whatever owns selection state -- `setHistory`
 * is exposed too (`setHistory`/`setIsOversizedImport`), since the drawing-gesture handlers
 * (ring/chain/shape/curved-arrow placement, and the paper-size-change handler) need direct
 * access to build their own no-op-detection logic on top of `history`, not just the generic
 * commit-or-not shape `commitDocument` provides.
 */
// #772 -- the page belongs to the drawing, not to its content: replacing the content (an import, or
// re-creating an empty drawing for a new paper size) keeps the page unless the new content has one.
function keepPage(next: DrawingDocument, current: DrawingDocument): DrawingDocument {
  return next.page || !current.page ? next : { ...next, page: current.page };
}

export function useDocumentHistory(paperSize: PaperSize, setSelection: (next: EditorSelection) => void) {
  const [history, setHistory] = useState<DocumentHistory>(() =>
    createDocumentHistory(createEmptyDrawingDocument(paperSize))
  );
  const [isOversizedImport, setIsOversizedImport] = useState(false);
  // #805 -- the page grows and shrinks with the content: the document everyone reads (save, export,
  // the paper) carries its current extent. Derived on read, so undo and redo stay consistent.
  const document = useMemo(() => withPageExtent(history.present), [history.present]);

  const commitDocument = useCallback((change: (current: DrawingDocument) => DrawingDocument) => {
    setHistory((current) => {
      const next = change(current.present);
      return commitHistory(current, next);
    });
  }, []);

  /** Updates the present document without creating an undo entry (for derived-state sync). */
  const applyWithoutHistory = useCallback((change: (current: DrawingDocument) => DrawingDocument) => {
    setHistory((current) => {
      const next = change(current.present);
      return next === current.present ? current : { ...current, present: next };
    });
  }, []);

  // #859 (#773 C6) -- a live adjustment (dragging an opacity slider, picking a colour): every step
  // is previewed on the present document without an undo entry; commitPreview records the whole
  // adjustment as ONE undo step from where it started, cancelPreview (Escape) restores that start.
  const presentRef = useRef(history.present);
  useLayoutEffect(() => {
    presentRef.current = history.present;
  }, [history.present]);
  const previewBase = useRef<DrawingDocument | null>(null);

  const previewDocument = useCallback((change: (current: DrawingDocument) => DrawingDocument) => {
    previewBase.current ??= presentRef.current;
    setHistory((current) => {
      const next = change(current.present);
      return next === current.present ? current : { ...current, present: next };
    });
  }, []);

  const commitPreview = useCallback(() => {
    const base = previewBase.current;
    previewBase.current = null;
    if (!base) return;
    setHistory((current) =>
      current.present === base ? current : commitHistory({ ...current, present: base }, current.present)
    );
  }, []);

  const cancelPreview = useCallback(() => {
    const base = previewBase.current;
    previewBase.current = null;
    if (base) setHistory((current) => ({ ...current, present: base }));
  }, []);

  const replaceDocument = useCallback(
    (next: DrawingDocument) => {
      setHistory(createDocumentHistory(next));
      setSelection(emptySelection());
      setIsOversizedImport(false);
    },
    [setSelection]
  );

  const resetDocument = useCallback(
    (size: PaperSize) => {
      replaceDocument(createEmptyDrawingDocument(size));
    },
    [replaceDocument]
  );

  /**
   * Commits a document produced by createDrawingDocumentFromChemicalStructure (SMILES import) as
   * an undo-able step - unlike replaceDocument (used for loading a JSON file or a saved drawing,
   * which intentionally starts a fresh undo history), an import should remain undo-able back to
   * whatever was on the canvas before it, matching commitDocument's existing behavior elsewhere.
   * Also flags the result as oversized when its coordinate space needed more room than the
   * paper it was imported into - see the isOversizedImport comment above for why that can't
   * just be derived reactively from the current coordinateSize/paperSize.
   */
  const commitImportedDocument = useCallback(
    (next: DrawingDocument, importPaperSize: PaperSize) => {
      commitDocument((current) => keepPage(next, current));
      setIsOversizedImport(
        next.depiction.coordinateSet.width > importPaperSize.width ||
          next.depiction.coordinateSet.height > importPaperSize.height
      );
    },
    [commitDocument]
  );

  const handlePaperSizeChange = useCallback((size: PaperSize) => {
    setHistory((current) =>
      current.present.structure.graph.nodes.length === 0 && current.present.annotations.length === 0
        ? { ...current, present: keepPage(createEmptyDrawingDocument(size), current.present) }
        : current
    );
  }, []);

  // A drawing shaped differently from the paper (one opened or imported from another window size)
  // is letterboxed inside it, and every move stopped at its edge -- an invisible wall partway across
  // the visible paper (owner report, 2026-09-26). So a drawing grows to the paper's shape: its
  // coordinate space extends right or down (never shrinks, so nothing is clipped); coordinates are
  // untouched, so everything tied to them (reaction boxes, Markush layers) stays aligned. A layout
  // adjustment, not an edit -- no undo step.
  const present = history.present;
  useEffect(() => {
    const { width, height } = present.depiction.coordinateSet;
    if (present.structure.graph.nodes.length === 0 && present.annotations.length === 0) return;
    const paperAspect = paperSize.width / paperSize.height;
    const aspect = width / height;
    if (!Number.isFinite(paperAspect) || Math.abs(paperAspect - aspect) / paperAspect < 0.005) return;
    const grown =
      paperAspect > aspect
        ? { width: Math.round(height * paperAspect), height }
        : { width, height: Math.round(width / paperAspect) };
    // Syncing to the paper's measured size (an external layout fact), once per mismatch.
    // eslint-disable-next-line react/set-state-in-effect
    applyWithoutHistory((current) =>
      current !== present
        ? current
        : {
            ...current,
            depiction: {
              ...current.depiction,
              coordinateSet: { ...current.depiction.coordinateSet, ...grown }
            }
          }
    );
  }, [applyWithoutHistory, paperSize, present]);

  const undo = useCallback(() => setHistory(undoHistory), []);
  const redo = useCallback(() => setHistory(redoHistory), []);

  return {
    document,
    history,
    isOversizedImport,
    setHistory,
    setIsOversizedImport,
    commitDocument,
    applyWithoutHistory,
    previewDocument,
    commitPreview,
    cancelPreview,
    replaceDocument,
    resetDocument,
    commitImportedDocument,
    undo,
    redo,
    handlePaperSizeChange
  };
}
